package com.vamvisions.main.theme

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.AttrsScope
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement

/** On `<html>` while reveal animations may run. Without it, [rememberReveal] elements are shown at once. */
const val REVEAL_GATE_CLASS = "js-reveal"

private external class IntersectionObserver(
    callback: (entries: Array<IntersectionObserverEntry>, observer: IntersectionObserver) -> Unit,
    options: dynamic = definedExternally,
) {
    fun observe(target: Element)
    fun disconnect()
}

private external interface IntersectionObserverEntry {
    val isIntersecting: Boolean
}

private val observerAvailable: Boolean
    get() = window.asDynamic().IntersectionObserver != undefined

/** Whether reveal animations may run now: the observer exists and the user does not ask for reduced motion. */
fun revealAllowed(reducedMotion: Boolean) = observerAvailable && !reducedMotion

/**
 * Sets [REVEAL_GATE_CLASS] before the first render, so revealed content never shows, hides and fades. Call it once,
 * before `renderComposable`.
 */
fun initReveal() {
    if (revealAllowed(window.matchMedia(REDUCED_MOTION_QUERY).matches)) {
        document.documentElement?.classList?.add(REVEAL_GATE_CLASS)
    }
}

/** Keeps [REVEAL_GATE_CLASS] in step with the reduced motion setting. Call it once, near the root. */
@Composable
fun RevealGate() {
    val reducedMotion by prefersReducedMotion()
    LaunchedEffect(reducedMotion) {
        val root = document.documentElement ?: return@LaunchedEffect
        if (revealAllowed(reducedMotion)) root.classList.add(REVEAL_GATE_CLASS) else root.classList.remove(REVEAL_GATE_CLASS)
    }
}

/**
 * Attributes that fade the element in and raise it 16px the first time it scrolls into view. [delayMs] staggers
 * neighbours, such as timeline cards. The revealed flag is Compose state, because Compose HTML rewrites `class` on
 * every attribute update. The attributes set the element's `ref`, so the element must not set its own.
 */
@Composable
fun rememberReveal(delayMs: Int = 0): AttrsScope<HTMLElement>.() -> Unit {
    var revealed by remember { mutableStateOf(!observerAvailable) }
    return {
        classes(AppStyleSheet.reveal)
        if (revealed) classes(AppStyleSheet.revealed)
        if (delayMs > 0) style { property("transition-delay", "${delayMs}ms") }
        ref { element ->
            // Threshold 0: a section taller than the viewport may never reach a larger visible share.
            val observer = if (revealed) {
                null
            } else {
                IntersectionObserver({ entries, observer ->
                    if (entries.any { it.isIntersecting }) {
                        revealed = true
                        observer.disconnect()
                    }
                }, js("({ threshold: 0, rootMargin: '0px 0px -10% 0px' })"))
            }
            observer?.observe(element)
            onDispose { observer?.disconnect() }
        }
    }
}
